// =============================================================================
// src/chrome/copilot-sidebar/scroll-spy-observer.ts
// Scroll-spy (IntersectionObserver) + nav clicks + acao primaria + Ctrl+J.
//
// Proveniencia: porte FIEL do IIFE inline de unified.html linhas 3033-3086.
//   O observer escolhe a secao mais proxima do centro da viewport e chama
//   updateInsight; cliques no nav-rail suprimem o observer por 1400ms; a acao
//   primaria reusa sendPrompt; "Nova pergunta"/Ctrl+J abrem o chat.
//
// Integracao com o bus: alem de chamar o painel, emitimos 'section:change' no
//   AppBus para os demais modulos (ex.: CopilotTactics) reagirem — substitui o
//   monkey-patch de SectionManager.activateSection do original (unified-app.js
//   2696-2700) por pub/sub tipado, mantendo o efeito visual identico.
//
// SRP: so a deteccao de secao + atalhos. O painel troca em insight-panel-controller.
// =============================================================================

import type { AppContext, SectionId } from '../../app/types';
import type { CopilotElementRefs } from './copilot-element-refs';
import type { CopilotInsightPanelController } from './insight-panel-controller';

/** Janela em que o observer fica suprimido apos clique no nav (3057: 1400ms). */
const OBSERVER_SUPPRESS_MS = 1400;

/** Thresholds e rootMargin do IntersectionObserver (unified.html 3048). */
const OBSERVER_THRESHOLDS: readonly number[] = [0.25, 0.45, 0.65];
const OBSERVER_ROOT_MARGIN = '-25% 0px -25% 0px';

/**
 * Liga o scroll-spy e os atalhos da sidebar do Copiloto.
 *
 * @param refs Referencias de DOM da sidebar.
 * @param panel Painel de contexto que recebe as trocas de secao.
 * @param ctx Container de DI (bus para 'section:change', commands para acoes).
 * @param reduceMotion Se true, scroll instantaneo (sem smooth) ao clicar no nav.
 */
export function bindCopilotScrollSpy(
  refs: CopilotElementRefs,
  panel: CopilotInsightPanelController,
  ctx: AppContext,
  reduceMotion: boolean,
): void {
  const spy = new ScrollSpyBinding(refs, panel, ctx, reduceMotion);
  spy.observeSections();
  spy.bindNavClicks();
  spy.bindPrimaryAction();
  spy.bindNewChat();
  spy.bindNovaPerguntaShortcut();
}

/** Encapsula o estado do scroll-spy (supressao temporaria do observer). */
class ScrollSpyBinding {
  private suppressObserverUntil = 0;

  constructor(
    private readonly refs: CopilotElementRefs,
    private readonly panel: CopilotInsightPanelController,
    private readonly ctx: AppContext,
    private readonly reduceMotion: boolean,
  ) {}

  /** Observa as .u-section[data-section] e troca para a mais central (3033-3050). */
  observeSections(): void {
    const sections = Array.from(
      document.querySelectorAll<HTMLElement>('.u-section[data-section]'),
    );
    if (!('IntersectionObserver' in window) || !sections.length) return;
    const visible = new Map<Element, number>();
    const observer = new IntersectionObserver(
      (entries) => this.onIntersect(entries, visible),
      { threshold: [...OBSERVER_THRESHOLDS], rootMargin: OBSERVER_ROOT_MARGIN },
    );
    sections.forEach((section) => observer.observe(section));
  }

  /** Callback do observer: escolhe a secao de maior intersectionRatio (3037-3047). */
  private onIntersect(
    entries: readonly IntersectionObserverEntry[],
    visible: Map<Element, number>,
  ): void {
    entries.forEach((entry) => {
      if (entry.isIntersecting) visible.set(entry.target, entry.intersectionRatio);
      else visible.delete(entry.target);
    });
    if (Date.now() < this.suppressObserverUntil) return;
    const best = pickMostVisibleSection(visible);
    if (best) this.changeSection(best);
  }

  /**
   * Cliques no nav-rail rolam ate a secao + atualizam o insight (3052-3061).
   * DESVIO PRESERVADO: navItems e vazio (`.copilot-nav-item` inexistente), logo
   *   este forEach nao liga nenhum handler — igual ao original.
   */
  bindNavClicks(): void {
    this.refs.navItems.forEach((navItem) => {
      navItem.addEventListener('click', () => this.onNavClick(navItem));
    });
  }

  /** Trata clique de um nav-item: scroll suave + supressao + troca de secao. */
  private onNavClick(navItem: HTMLElement): void {
    const sectionId = navItem.dataset.section as SectionId | undefined;
    if (!sectionId) return;
    const target = document.getElementById('section-' + sectionId);
    this.suppressObserverUntil = Date.now() + OBSERVER_SUPPRESS_MS;
    if (target)
      target.scrollIntoView({
        behavior: this.reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    this.changeSection(sectionId);
  }

  /** Troca o painel e emite 'section:change' no bus (substitui o monkey-patch). */
  private changeSection(sectionId: SectionId): void {
    this.panel.updateInsight(sectionId);
    this.ctx.bus.emit('section:change', { section: sectionId });
  }

  /** Acao primaria abre o CHAT REAL com o data-prompt da secao.
   *  Reforma 2026-06 (Fase 4): antes era sendPrompt -> toast morto; "ver
   *  estratégia" agora entrega a estrategia de verdade no copilot-chat. */
  bindPrimaryAction(): void {
    const { actionPrimary } = this.refs;
    if (!actionPrimary) return;
    actionPrimary.addEventListener('click', () => {
      const prompt = actionPrimary.dataset.prompt || '';
      if (prompt) this.ctx.commands.openCopilotChat(prompt);
    });
  }

  /** "Nova pergunta" abre o chat sem prompt pre-preenchido (3071-3077). */
  bindNewChat(): void {
    const { newChat } = this.refs;
    if (!newChat) return;
    newChat.addEventListener('click', () => this.ctx.commands.openCopilotChat(''));
  }

  /** Ctrl+J / Cmd+J dispara "Nova pergunta" (3079-3086). */
  bindNovaPerguntaShortcut(): void {
    document.addEventListener('keydown', (keyEvent) => {
      if (!isNovaPerguntaCombo(keyEvent)) return;
      keyEvent.preventDefault();
      if (this.refs.newChat) this.refs.newChat.click();
    });
  }
}

/** Devolve a secao visivel de maior intersectionRatio (porte de 3043-3046). */
function pickMostVisibleSection(visible: Map<Element, number>): SectionId | null {
  if (!visible.size) return null;
  let bestSection: SectionId | null = null;
  let bestRatio = -1;
  visible.forEach((ratio, element) => {
    if (ratio <= bestRatio) return;
    bestRatio = ratio;
    bestSection = (element as HTMLElement).dataset.section as SectionId;
  });
  return bestSection;
}

/** True para Ctrl+J ou Cmd+J (porte da guarda de tecla, 3082). */
function isNovaPerguntaCombo(keyEvent: KeyboardEvent): boolean {
  return (keyEvent.ctrlKey || keyEvent.metaKey) && keyEvent.key.toLowerCase() === 'j';
}
